import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { useDialog, usePreventScroll } from 'react-aria';
import type { OverlayTriggerState } from 'react-stately';

import type { ProfileData } from '@/app/[locale]/leadership/_data/profiles';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import CloseButton from './CloseButton';
import ScrollTeaser from './ScrollTeaser';

export interface ModalContentsProps extends OverlayTriggerState {
  readonly profile: ProfileData;
}

const ProfileImage = ({
  imageUrl,
  name,
}: Pick<ProfileData, 'imageUrl' | 'name'>) => (
  <Image
    alt={name}
    className="w-full"
    data-cy="leadership_profile_modal_photo"
    height={877}
    src={`${imageUrl}.jpg?ar=540:617&crop=focalpoint&fit=crop`}
    width={767}
  />
);

const emphasizedText = (chunks: React.ReactNode) => (
  <em className="font-serif italic">{chunks}</em>
);

// eslint-disable-next-line @typescript-eslint/unbound-method
const ModalContents = ({ close, profile }: ModalContentsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollEndTimerRef = useRef<NodeJS.Timeout>(undefined);
  const t = useTranslations();
  const { dialogProps, titleProps } = useDialog({}, wrapperRef);
  const { scrollYProgress } = useScroll({
    container: wrapperRef,
    offset: ['start start', 'end end'],
  });

  // Automatically close the modal when the user has scrolled to the bottom
  useMotionValueEvent(scrollYProgress, 'change', (latest: number): void => {
    // Delay close by one second to allow user to scroll up again if desired
    clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      if (latest > 0.99) {
        close();
      }
    }, 1000);
  });

  usePreventScroll();

  return (
    <div
      {...dialogProps}
      className="relative size-full overflow-y-auto bg-white pb-20 md:pt-32 md:pb-24 lg:pb-32"
      data-cy="leadership_profile_modal_dialog"
      ref={wrapperRef}
    >
      <CloseButton close={close} />
      <div className="md:hidden">
        <ProfileImage imageUrl={profile.imageUrl} name={profile.name} />
      </div>
      <LayoutGrid rowGap={32}>
        <GridItem
          className="hidden md:block"
          colEnd={{ md: 13, xxl: 15 }}
          colStart={{ md: 'main', lg: 6, xxl: 8 }}
        >
          <Motion
            initial={{ opacity: 0, y: 100 }}
            transition={{ delay: 0.25, duration: 0.35, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="pt-4 md:pb-10 xxl:pb-16">
              <ProfileImage imageUrl={profile.imageUrl} name={profile.name} />
            </div>
          </Motion>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'main', md: 23, xxl: 22 }}
          colStart={{ sm: 'main', md: 14, xxl: 16 }}
        >
          <Motion
            initial={{ opacity: 0, y: -100 }}
            transition={{ delay: 0.35, duration: 0.35, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="relative flex lg:h-full">
              <div
                aria-hidden="true"
                className="absolute top-0 left-[10px] h-32 w-px -translate-x-1/2 -translate-y-1/2 bg-gray-300 md:-top-32 md:left-0 md:translate-y-0 lg:left-16"
              />
              <div className="mt-auto pt-20 md:hidden lg:block">
                <ScrollTeaser
                  label="scroll_for_more"
                  textDirection="vertical"
                />
              </div>
              <div className="flex flex-1 flex-col pt-8 pl-4 md:pl-0 lg:pl-10 xxl:pl-12">
                <h2 {...titleProps}>
                  <Typography
                    className="mb-2 block text-xs tracking-widest uppercase"
                    Element="span"
                  >
                    {t(profile.titleKey)}
                  </Typography>
                  <Typography
                    className="block text-4xl"
                    Element="span"
                    weight="light"
                  >
                    {profile.name}
                  </Typography>
                </h2>
                <div className="pt-4 pl-8 md:pl-0">
                  <Typography
                    className="mb-2 text-sm tracking-widest uppercase"
                    Element="h3"
                    weight="bold"
                  >
                    {t('daily_habit')}
                  </Typography>
                  <Typography>
                    <RichText messageKey={profile.dailyHabitKey} />
                  </Typography>
                </div>
              </div>
            </div>
          </Motion>
        </GridItem>
        {profile.quoteKey && (
          <GridItem
            colEnd={{ sm: 'main', md: 23, lg: 22, xxl: 20 }}
            colStart={{ sm: 'main', md: 6, xxl: 8 }}
          >
            <Motion
              initial={{ opacity: 0 }}
              transition={{ delay: 0.5, duration: 0.35, type: 'tween' }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1 }}
            >
              <blockquote
                className="w-full md:border-l md:border-solid md:border-gray-300 md:py-10 md:pl-16 lg:pt-20 lg:pb-4 lg:pl-32"
                data-cy="leadership_profile_modal_quote"
              >
                <Typography
                  className="text-4xl leading-none before:content-[open-quote] after:content-[close-quote] md:text-6xl"
                  weight="light"
                >
                  <RichText
                    messageKey={profile.quoteKey}
                    tValues={{
                      em: emphasizedText,
                    }}
                  />
                </Typography>
              </blockquote>
            </Motion>
          </GridItem>
        )}
        <GridItem
          colEnd={{ sm: 'main', md: 22, lg: 21 }}
          colStart={{ sm: 'main', md: 10, lg: 12 }}
        >
          <Motion
            initial={{ opacity: 0 }}
            transition={{ duration: 0.35, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <Typography
              className="mb-2 text-sm tracking-widest uppercase"
              Element="h3"
              weight="bold"
            >
              {t('bio')}
            </Typography>
            <Typography>
              <RichText messageKey={profile.bioKey} />
            </Typography>
          </Motion>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'main', md: 16 }}
          colStart={{ sm: 'main', md: 10 }}
        >
          <div className="relative flex justify-center lg:justify-start">
            <div
              aria-hidden="true"
              className="absolute -bottom-8 left-[10px] hidden h-24 w-px -translate-x-1/2 translate-y-full bg-gray-300 md:block"
            />
            <div className="lg:hidden">
              <ScrollTeaser label="keep_scrolling_to_get_back" />
            </div>
            <div className="hidden lg:block">
              <ScrollTeaser label="scroll_for_more" textDirection="vertical" />
            </div>
          </div>
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default ModalContents;
